import styled, { css } from 'styled-components';
import { BasePortableTextProps, ArticlePortableTextProps } from './types';
import Theme from '.././theme';
import { Base } from './base';

export const PortableTextButton = styled(Base)``;
export const PortableTextLink = styled(Base)``;
export const PortableTextH1 = styled.p``;
export const PortableTextH2 = styled.p``;
export const PortableTextH3 = styled.p``;
export const PortableTextH4 = styled.p``;

export const ArticleHeadingStyles = css`
  h1,
  ${PortableTextH1} {
    ${Theme.typography.variants.primary.xl};

    ${Theme.mediaQueries.md} {
      font-size: ${Theme.typography.size.xxl};
      line-height: ${Theme.typography.lineHeight.xxl};
    }
  }

  h2,
  ${PortableTextH2} {
    ${Theme.typography.variants.primary.lg};
  }

  h3,
  h4,
  ${PortableTextH3}, ${PortableTextH4} {
    ${Theme.typography.variants.primary.md};
  }
`;

export const BasePortableText = styled.div<BasePortableTextProps>`
  h1,
  h2,
  h3,
  h4,
  p,
  ul,
  ol,
  li,
  ${PortableTextButton},
    ${PortableTextLink},
    ${PortableTextH1},
    ${PortableTextH2},
    ${PortableTextH3},
    ${PortableTextH4} {
    color: ${({ theme, textColor }) =>
      textColor ? theme.colors[textColor] : 'inherit'};
  }

  ul,
  ol {
    margin-bottom: 1.5rem;
  }

  ul > li {
    padding-left: 1rem;
    margin-bottom: 0.75rem;
    display: inline-flex;
    width: 100%;

    ::before {
      content: '•';
      transform: translate3d(-1rem, 0, 0);
    }
  }

  ol:first-child {
    counter-reset: customlistcounter;
  }

  ol > li {
    padding-left: 1rem;
    margin-bottom: 0.75rem;
    display: inline-flex;
    width: 100%;
    counter-increment: customlistcounter;

    ::before {
      content: counter(customlistcounter) ' ';
      transform: translate3d(-1rem, 0, 0);
    }
  }

  em {
    font-style: italic;
  }

  strong {
    font-weight: ${Theme.typography.weight.bold};
  }

  ${PortableTextButton},
  ${PortableTextLink} {
    display: inline;

    > span {
      ${({ theme, textColor = 'black' }) =>
        theme.hoverStyles.linkBorderBottom(textColor)};
    }

    :hover,
    :focus {
      > span {
        ${({ theme, textColor = 'black' }) =>
          theme.hoverStyles.linkBorderBottomHidden(textColor)};
      }
    }
  }
`;

export const ArticlePortableText = styled(BasePortableText)<
  ArticlePortableTextProps
>`
  ${ArticleHeadingStyles};

  ${({ fontStyleIsSecondaryMd = false }) =>
    fontStyleIsSecondaryMd
      ? Theme.typography.variants.secondary.md
      : Theme.typography.variants.secondary.sm};

  h1,
  ${PortableTextH1} {
    margin-bottom: 4.5rem;
  }

  h2,
  ${PortableTextH2} {
    margin-bottom: 4.5rem;
    margin-top: 3rem;
  }

  h3,
  ${PortableTextH3} {
    margin-bottom: 1.5rem;
    margin-top: 2.25rem;
  }

  h4,
  ${PortableTextH4} {
    margin-bottom: 1.5rem;
    margin-top: 2rem;
  }

  p {
    padding-bottom: 1.5rem;
  }

  p > .text-sans-serif {
    ${Theme.typography.variants.primary.sm};
  }
`;

export const TextModulePortableText = styled(BasePortableText)`
  ${ArticleHeadingStyles};

  h1,
  ${PortableTextH1} {
    margin-bottom: 4.5rem;
  }

  h2,
  ${PortableTextH2}, h3,
  ${PortableTextH3}, h4,
  ${PortableTextH4} {
    margin-bottom: 1.5rem;
    margin-top: 2rem;
  }

  p {
    &:not(:last-child) {
      margin-bottom: 1.5rem;
    }
  }

  ul,
  ol {
    margin-bottom: 0;
  }
`;

export const ArticleCalloutPortableText = styled(BasePortableText)`
  ${Theme.typography.variants.secondary.sm};

  && {
    p {
      padding-bottom: 0;
    }

    ol,
    ul {
      margin-bottom: 0;
    }

    h1,
    ${PortableTextH1}, h2,
    ${PortableTextH2}, h3,
    ${PortableTextH3}, h4,
    ${PortableTextH4} {
      margin-top: 0;
      margin-bottom: 0;
    }

    ${PortableTextH2}, h2 {
      ${Theme.typography.variants.primary.md};

      ${Theme.mediaQueries.lg} {
        ${Theme.typography.variants.primary.lg};
      }
    }
  }
`;

export const ArticleHighlightedTextModulePortableText = styled(
  BasePortableText
)`
  ${Theme.typography.variants.secondary.md}
  ${ArticleHeadingStyles};

  h1,
  ${PortableTextH1} {
    margin-bottom: 2rem;
  }

  h2,
  ${PortableTextH2}, h3,
  ${PortableTextH3}, h4,
  ${PortableTextH4} {
    margin-top: 2rem;
    margin-bottom: 2rem;
  }

  && {
    p {
      padding-bottom: 1rem;
    }

    ul,
    ol {
      margin-bottom: 0;
    }
  }
`;

export const AccordionListModulePortableText = styled(BasePortableText)`
  ${ArticleHeadingStyles};

  p {
    padding-bottom: 1.5rem;
  }

  h1,
  ${PortableTextH1}, h2,
  ${PortableTextH2}, h3,
  ${PortableTextH3}, h4,
  ${PortableTextH4} {
    margin-top: 1rem;
  }
`;

export const MarketingPortableText = styled(BasePortableText)`
  p {
    padding-bottom: 1.5rem;
  }

  h1,
  ${PortableTextH1}, h2,
  ${PortableTextH2} {
    ${Theme.typography.variants.primary.xl};
    margin-bottom: 4.5rem;
  }

  h2,
  ${PortableTextH2} {
    margin-top: 3rem;
  }

  h3,
  ${PortableTextH3} {
    ${Theme.typography.variants.primary.lg};
    margin-bottom: 1.5rem;
    margin-top: 2.25rem;
  }

  h4,
  ${PortableTextH4} {
    ${Theme.typography.variants.primary.md};
    margin-bottom: 1.5rem;
  }
`;
